/**
 * Property Service - Refactored with Unified Architecture
 * Migrated from mixed axios/fetch to unified fetch-based architecture
 * Follows SOLID, KISS, and YAGNI principles
 */

import { CACHE_TTL } from "@/config/cacheLifetime.config";
import { CacheTag } from "@/enums/cacheTags";
import {
  DevelopersApiResponse,
  MainParams,
  PropertyParams,
  PropertySlugParams,
  PropertyUrlParams,
  SitemapParams,
} from "@/types/property.types";

import { formatTitleByLocale } from "@/data/helper/formatTitleByLocale";
import {
  flattenPropertyData,
  StandardProperty,
} from "@/types/standardProperty.types";
import { BaseService } from "./core/base-service";
import { NotFoundError } from "./core/errors";
import { IHiddenPhones } from "@/types/hiddenPhones.types";
import { HiddenContactsEnumEndpoint } from "@/types/enums";
import { axiosWithAuth } from "@/services/legacy/api/axiosInstances";

// Enhanced type definitions for property responses
export interface PropertyResponse {
  id: string;
  title: string;
  description?: string;
  price: number;
  currency: string;
  location: {
    city: string;
    region: string;
    country: string;
    coordinates?: [number, number];
  };
  images: string[];
  features: Record<string, any>;
  dealType: string;
  category: string;
  createdAt: string;
  updatedAt: string;
}

export interface PropertyListResponse {
  properties: PropertyResponse[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  filters?: Record<string, any>;
}

// Table data interfaces
export interface TableDataTransformOptions {
  locale: string;
}

export interface PropertyTableItem {
  id: string;
  customId?: string;
  slug?: string;
  title: string;
  description?: string;
  translations?: any[];
  // Standardized fields (using flattened structure)
  rooms?: number;
  area?: number;
  floor?: number;
  floors?: number;
  bedrooms?: number;
  bathrooms?: number;
  city?: string;
  address?: string;
  district?: string;
  microdistrict?: string;
  // Pricing (preserved structure)
  pricing?: {
    totalPrice?: number;
    convertedPricesMin?: any;
    convertedPricesMax?: any;
    convertedPrices?: any;
  };
  // Realtor fields
  realtorFields?: {
    assignedRealtor?: any;
    assignedRealtorName?: string;
    ownerPhone?: string;
    callDate?: string;
    callStatus?: string;
  };
  // Additional fields
  images?: string[];
  publicationStatus?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface PropertyCountResponse {
  total: number;
  category: string;
}

// Configuration constants
const DEFAULT_PAGE_SIZE = parseInt(process.env.PAGE_SIZE || "12", 10);
const DEFAULT_CACHE_TTL = CACHE_TTL[CacheTag.PROPERTY_COLLECTION] || 3600;

/**
 * Property Service Class
 * Handles all property-related operations
 * Single Responsibility: Property data management
 */
class PropertyService extends BaseService {
  constructor() {
    // Get timeout from environment variables with proper fallback
    const envTimeout = 8000; // 3s for all environments - fast fail approach

    super({
      name: "property",
      baseURL: process.env.NEXT_PUBLIC_BASE_URL,
      timeout: envTimeout, // Use environment timeout configuration
      retries: 3,
      retryDelay: 1000,
      enableLogging: true,
      enableMetrics: true,
      cache: {
        enabled: true,
        defaultTtl: CACHE_TTL.SHORT, // 5 minutes for property data
        strategy: "cache-first",
      },
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
    });
  }

  /**
   * Get detailed property information by ID
   */
  async getProperty(params: PropertyParams): Promise<PropertyResponse> {
    this.validateRequired(params, ["propertyId"]);

    try {
      return await this.get<PropertyResponse>(
        "/property/property-info",
        params,
      );
    } catch (error) {
      this.handleError(error, "get property details");
    }
  }

  /**
   * Fetch property details with Next.js caching
   * Uses native fetch with ISR caching for better performance
   */
  async fetchPropertyDetails(
    params: PropertyParams,
  ): Promise<PropertyResponse> {
    this.validateRequired(params, ["propertyId"]);

    const queryParams = {
      propertyId: params.propertyId,
      ...(params.dealType && { dealType: params.dealType.toString() }),
    };

    try {
      // NOTE: Removed force-cache to allow property data updates
      // Caching is handled by Next.js ISR at page level
      const response = await this.client.get<PropertyResponse>(
        "/property/property-info",
        { params: queryParams },
      );

      return response.data;
    } catch (error) {
      this.handleError(error, "fetch property details with caching");
    }
  }

  /**
   * Get recommended properties (EXACT API match)
   */
  async getRecommendedProperty(params?: Pick<MainParams, "limit">) {
    const url = "/property/recommended-property";
    try {
      const response = await this.client.get(url, {
        params,
      });
      return response.data;
    } catch (error: any) {
      const errorMessage = this.extractErrorMessage(error);
      this.logger.error(
        `[getRecommendedProperty] Failed with params: ${JSON.stringify(params)}. Error: ${errorMessage}`,
        { params, error },
      );
      throw error;
    }
  }

  /**
   * Get all properties with pagination and filtering (EXACT API match)
   */
  async getAllProperty(params: MainParams) {
    const url = "/property/all-property";
    // Use PAGE_SIZE as default limit if not provided
    const paramsWithDefaults = {
      ...params,
      limit: params.limit || DEFAULT_PAGE_SIZE,
    };

    try {
      const response = await this.client.get(url, {
        params: paramsWithDefaults,
      });
      return response.data;
    } catch (error: any) {
      const errorMessage = this.extractErrorMessage(error);
      this.logger.error(
        `[getAllProperty] Failed with params: ${JSON.stringify(params)}. Error: ${errorMessage}`,
        { params, error },
      );
      throw error;
    }
  }

  /**
   * Get property count by category (EXACT API match)
   */
  async fetchPropertyCount(
    params: {
      category: string;
    },
    cacheTag: CacheTag,
  ): Promise<{ total: number }> {
    const queryString = new URLSearchParams({
      category: params.category,
      limit: "1",
      page: "1",
    }).toString();
    const url = `${process.env.NEXT_PUBLIC_BASE_URL}/property/all-property?${queryString}`;

    try {
      const fetchOptions: RequestInit = {
        // Use service timeout configuration
        signal: AbortSignal.timeout(8000),
      };

      // Only enable Next.js caching in runtime mode, not during build
      if (process.env.NEXT_BUILD_MODE !== "true") {
        fetchOptions.cache = 'no-cache';
        fetchOptions.next = {
          // revalidate: DEFAULT_CACHE_TTL,
          tags: [cacheTag || CacheTag.PROPERTY_COLLECTION], // Different tag to prevent cache conflicts
        };
      }

      const response = await fetch(url, fetchOptions);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      return { total: data.total || 0 };
    } catch (error: any) {
      const errorMessage = this.extractErrorMessage(error);
      this.logger.error(`[fetchPropertyCount] Failed: ${errorMessage}`);
      // Return empty count on timeout/error - allows build to continue
      return { total: 0 };
    }
  }

  /**
   * Fetch all properties (EXACT API match)
   */
  async fetchAllProperty(
    params: MainParams,
    cacheTag: CacheTag,
  ): Promise<DevelopersApiResponse> {
    const limit = params.limit || DEFAULT_PAGE_SIZE;

    const queryString = new URLSearchParams({
      category: params.category,
      limit: limit.toString(),
      ...(params.page && { page: params.page.toString() }),
    }).toString();

    const url = `${process.env.NEXT_PUBLIC_BASE_URL}/property/all-property?${queryString}`;

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);

    try {
      const response = await axiosWithAuth(url, {
        // cache: "no-store",
        // next: {
        //   // revalidate: DEFAULT_CACHE_TTL,
        //   tags: [cacheTag || CacheTag.PROPERTY_COLLECTION],
        // },
        signal: controller.signal,
      });

      clearTimeout(timeout);

      if (!response) {
        // const errorBody = await response.text();
        // console.error(`[fetchAllProperty] ${response.status}`, errorBody);

        return {
          data: [],
          total: 0,
          page: params.page || 1,
          limit,
          totalPages: 0,
        };
      }

      return response.data
    } catch (error: any) {
      clearTimeout(timeout);

      console.error(`[fetchAllProperty]`, error);

      return {
        data: [],
        total: 0,
        page: params.page || 1,
        limit,
        totalPages: 0,
      };
    }
  }

  /**
   * Fetch property details by legacy URL slug (EXACT API match)
   */
  async fetchPropertyDetailsByUrl(params: PropertyUrlParams) {
    const queryString = new URLSearchParams({
      propertyUrl: params.propertyUrl,
      ...(params.dealType && { dealType: params.dealType.toString() }),
    }).toString();
    const url = `${process.env.NEXT_PUBLIC_BASE_URL}/property/property-info-url?${queryString}`;

    try {
      const response = await fetch(url, {
        cache: "no-store",
        next: {
          // revalidate: DEFAULT_CACHE_TTL,
          tags: [CacheTag.PROPERTY_COLLECTION],
        },
      });

      if (!response.ok) {
        let errorDataJson: any = null;
        let errorBodyText = "";
        try {
          const tempBody = await response.text();
          errorBodyText = tempBody;
          errorDataJson = JSON.parse(tempBody);
        } catch (e) {
          console.error(e);
          // errorDataJson remains null, errorBodyText has the content
        }

        const status = response.status;
        const statusText = response.statusText;
        const extractedMessage =
          (typeof errorDataJson === "object" && errorDataJson?.message) ||
          `Status: ${status} ${statusText}`;
        const logDetails = {
          params,
          status,
          statusText,
          errorBody: errorBodyText,
          errorDataJson,
          url,
          originalErrorSource: "fetch",
        };

        this.logger.error(
          `[fetchPropertyDetailsByUrl] Failed. URL: ${url}`,
          logDetails,
        );
        const serviceErrorMessage = `Failed to fetch property by URL. ${extractedMessage}`;

        throw new Error(serviceErrorMessage);
      }

      return response.json();
    } catch (error: any) {
      if (error.message?.includes("Failed to fetch property by URL")) {
        throw error;
      } // Already handled

      this.logger.error(
        `[fetchPropertyDetailsByUrl] Unhandled error with params: ${JSON.stringify(params)}. Error: ${error.message}`,
        { params, originalError: error, originalErrorSource: "fetch_unknown" },
      );

      throw new Error(
        `Unhandled error in fetchPropertyDetailsByUrl: ${error.message}`,
      );
    }
  }

  /**
   * Get property details for authenticated user using show-one endpoint
   * This endpoint requires authentication and is used for edit mode
   */
  async getPropertyShowOne(slug: string): Promise<any> {
    try {
      // This will automatically include auth headers via interceptors
      const response = await this.get<any>("/property/show-one", { slug });
      return response;
    } catch (error) {
      this.handleError(error, "get property for show-one endpoint");
    }
  }

  async getPropertyHiddenFields(
    id: string,
    type: HiddenContactsEnumEndpoint,
  ): Promise<IHiddenPhones[] | undefined> {
    try {
      return await this.get<IHiddenPhones[]>(`/properties/${id}/${type}`);
    } catch (error) {
      return this.handleError(error, "get property for show-one endpoint");
    }
  }

  /**
   * Fetch property details by slug with fast-fail 404 handling and native Next.js caching
   * MINIMAL FIX: Removed custom cacheManager wrapper, using native Next.js Data Cache
   */
  async fetchPropertyBySlug(params: PropertySlugParams) {
    const queryString = new URLSearchParams({
      slug: params.slug,
      ...(params.dealType && { dealType: params.dealType.toString() }),
    }).toString();
    const url = `${process.env.NEXT_PUBLIC_BASE_URL}/property/find-by-slug?${queryString}`;

    const { cookies } = await import("next/headers");
    const cookieStore = await cookies();
    const cookieHeader = cookieStore.toString();

    try {
      const response = await axiosWithAuth(url, {
        headers: {
          Cookie: cookieHeader,
        },
      });

      if (!response) {
          console.error('Error');
          return
      }

      return response.data
    } catch (error: any) {
      // Re-throw NotFoundError and other known errors
      if (
        error instanceof NotFoundError ||
        error.message?.includes("Failed to fetch property by slug")
      ) {
        throw error;
      }

      this.logger.error(
        `[fetchPropertyBySlug] Unhandled error with params: ${JSON.stringify(params)}. Error: ${error.message}`,
        { params, originalError: error, originalErrorSource: "fetch_unknown" },
      );

      throw new Error(
        `Unhandled error in fetchPropertyBySlug: ${error.message}`,
      );
    }
  }

  /**
   * Get properties by developer
   */
  async getPropertiesByDeveloper(
    developerId: string,
    params?: Partial<MainParams>,
  ): Promise<DevelopersApiResponse> {
    this.validateRequired({ developerId }, ["developerId"]);

    const queryParams = {
      developerId,
      ...params,
      limit: params?.limit || DEFAULT_PAGE_SIZE,
    };

    try {
      return await this.get<DevelopersApiResponse>(
        "/property/by-developer",
        queryParams,
      );
    } catch (error) {
      this.handleError(error, "get properties by developer");
    }
  }

  /**
   * Get property by slug for SEO-friendly URLs
   */
  async getPropertyBySlug(
    params: PropertySlugParams,
  ): Promise<PropertyResponse> {
    this.validateRequired(params, ["slug"]);

    try {
      return await this.get<PropertyResponse>("/property/by-slug", params);
    } catch (error) {
      this.handleError(error, "get property by slug");
    }
  }

  /**
   * Get properties by URL parameters for routing
   */
  async getPropertiesByUrl(
    params: PropertyUrlParams,
  ): Promise<PropertyListResponse> {
    try {
      return await this.get<PropertyListResponse>("/property/by-url", params);
    } catch (error) {
      this.handleError(error, "get properties by URL parameters");
    }
  }

  /**
   * Get featured properties for homepage
   */
  async getFeaturedProperties(limit = 6): Promise<PropertyResponse[]> {
    try {
      const response = await this.get<{ properties: PropertyResponse[] }>(
        "/property/featured",
        { limit },
      );

      return response.properties || [];
    } catch (error) {
      this.handleError(error, "get featured properties");
    }
  }

  /**
   * Get property statistics for analytics
   */
  async getPropertyStats(): Promise<{
    totalProperties: number;
    totalByCategory: Record<string, number>;
    totalByDealType: Record<string, number>;
    averagePrice: number;
  }> {
    try {
      return await this.get<{
        totalProperties: number;
        totalByCategory: Record<string, number>;
        totalByDealType: Record<string, number>;
        averagePrice: number;
      }>("/property/statistics");
    } catch (error) {
      this.handleError(error, "get property statistics");
    }
  }

  /**
   * Validate property search parameters
   */
  private validateSearchParams(params: any): void {
    if (
      params.priceMin &&
      params.priceMax &&
      params.priceMin > params.priceMax
    ) {
      this.handleError(
        {
          message: "Minimum price cannot be greater than maximum price",
          code: "INVALID_PRICE_RANGE",
          details: { priceMin: params.priceMin, priceMax: params.priceMax },
        },
        "price range validation",
      );
    }

    if (params.page && params.page < 1) {
      this.handleError(
        {
          message: "Page number must be greater than 0",
          code: "INVALID_PAGE",
          details: { page: params.page },
        },
        "page validation",
      );
    }

    if (params.limit && (params.limit < 1 || params.limit > 100)) {
      this.handleError(
        {
          message: "Limit must be between 1 and 100",
          code: "INVALID_LIMIT",
          details: { limit: params.limit },
        },
        "limit validation",
      );
    }
  }

  /**
   * Extract error message from error object (EXACT compatibility match)
   */
  private extractErrorMessage(error: any): string {
    if (error?.response?.data?.message) {
      return error.response.data.message;
    }
    if (error?.message) {
      return error.message;
    }
    return "Unknown error";
  }

  /**
   * Enhanced validation for property parameters
   */
  protected validateRequired(
    params: Record<string, any>,
    requiredFields: string[],
  ): void {
    // Run base validation
    super.validateRequired(params, requiredFields);

    // Additional property-specific validation
    this.validateSearchParams(params);
  }

  /**
   * Custom health check for property service
   */
  async healthCheck() {
    try {
      // Quick health check with minimal data
      await this.getPropertyCount({ category: "residential" });

      return {
        service: this.serviceName,
        status: "healthy" as const,
        timestamp: new Date().toISOString(),
        endpoints: {
          search: "healthy",
          details: "healthy",
          count: "healthy",
        },
      };
    } catch (error) {
      return {
        service: this.serviceName,
        status: "unhealthy" as const,
        timestamp: new Date().toISOString(),
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  /**
   * Fetch properties with layouts for sitemap generation
   * Uses server-side sitemap API for optimal performance
   */
  async fetchPropertiesForSitemap(
    params: SitemapParams,
  ): Promise<DevelopersApiResponse> {
    const queryString = new URLSearchParams({
      category: params.category,
      ...(params.limit && { limit: params.limit.toString() }),
      ...(params.page && { page: params.page.toString() }),
      ...(params.includeLayouts && { includeLayouts: "true" }),
    }).toString();
    const url = `${process.env.NEXT_PUBLIC_BASE_URL}/property/sitemap-data?${queryString}`;

    try {
      const response = await fetch(url, {
        cache: "no-store",
        next: {
          // revalidate: DEFAULT_CACHE_TTL,
          tags: [CacheTag.PROPERTY_COLLECTION],
        },
        signal: AbortSignal.timeout(8000),
      });

      if (!response.ok) {
        const errorBody = await response.text();
        const status = response.status;
        const statusText = response.statusText;

        console.error(`[fetchPropertiesForSitemap] Failed. URL: ${url}`, {
          status,
          statusText,
          errorBody,
        });

        // Fallback to regular fetchAllProperty if sitemap endpoint fails
        return this.fetchAllProperty(params);
      }

      const data = await response.json();

      return data;
    } catch (error) {
      console.error(
        `[fetchPropertiesForSitemap] Error with params: ${JSON.stringify(params)}. Error: ${error instanceof Error ? error.message : "Unknown error"}`,
      );

      // Fallback to regular fetchAllProperty
      return this.fetchAllProperty(params);
    }
  }

  /**
   * Transform data for table view - Migrated from TableDataService
   */
  static transformTableData(
    data: any,
    options: TableDataTransformOptions,
  ): PropertyTableItem[] {
    if (!data?.data) return [];

    return data.data.map((item: StandardProperty) => {
      const flattened = flattenPropertyData(item);

      return {
        ...item,
        // Localize title and description
        title: formatTitleByLocale({
          title: item.title || "",
          translations: item.translations || [],
          locale: options.locale,
          key: "title",
        }),
        description: flattened.description
          ? formatTitleByLocale({
              title: item.description,
              translations: item.translations || [],
              locale: options.locale,
              key: "description",
            })
          : undefined,
        // Include createdAt for table display
        createdAt: item.createdAt,
      } as PropertyTableItem;
    });
  }

  /**
   * Utility methods for table operations - Migrated from TableDataService
   */
  static getPropertyKey(property: PropertyTableItem): string {
    return property.id || property.customId || property.title || "unknown";
  }

  static getTableImage(property: PropertyTableItem): string | undefined {
    return property.images?.[0];
  }

  static getPropertySlug(property: PropertyTableItem): string | undefined {
    return property.slug;
  }

  static getDisplayId(property: PropertyTableItem): string | undefined {
    return property.customId || property.id;
  }

  static getFloorDisplay(property: PropertyTableItem): string {
    const { floor, floors } = property;
    if (floor && floors) return `${floor}/${floors}`;
    if (floor) return `${floor}`;
    if (floors) return `${floors}`;
    return "";
  }

  static hasAssignedAgent(property: PropertyTableItem): boolean {
    return !!property.realtorFields?.assignedRealtor;
  }

  static getMaskedOwnerPhone(property: PropertyTableItem): string | undefined {
    const phone = property.realtorFields?.ownerPhone;
    if (!phone) return undefined;

    // Mask phone: show first 3 and last 2 digits
    if (phone.length >= 6) {
      const start = phone.substring(0, 3);
      const end = phone.substring(phone.length - 2);
      const middle = "*".repeat(phone.length - 5);
      return `${start}${middle}${end}`;
    }
    return phone;
  }
}

// Export singleton instance
export const propertyService = new PropertyService();

// Export for backward compatibility
export default propertyService;

// Export types
export type { PropertyCountResponse, PropertyListResponse, PropertyResponse };
